import { Plugin, PluginKey, Transaction } from 'prosemirror-state';
import { Node } from 'prosemirror-model';
import { ReplaceAroundStep } from 'prosemirror-transform';
import { schema } from '../schema';

export const headingPluginKey = new PluginKey('heading');

interface HeadingInfo {
  firstHeadingIsH1: boolean;
  h1Count: number;
}

/**
 * Analyzes heading structure in a single traversal.
 */
function analyzeHeadings(doc: Node): HeadingInfo {
  let h1Count = 0;
  let firstHeadingLevel: number | null = null;

  doc.descendants((node) => {
    if (node.type.name === 'heading') {
      if (firstHeadingLevel === null) {
        firstHeadingLevel = node.attrs.level;
      }
      if (node.attrs.level === 1) {
        h1Count++;
      }
    }
    return true;
  });

  return {
    firstHeadingIsH1: firstHeadingLevel === 1,
    h1Count,
  };
}

/**
 * Validates heading structure.
 * Rules:
 * - First heading must be H1
 * - Only one H1 allowed
 */
function isValidHeadingStructure(info: HeadingInfo): boolean {
  return info.firstHeadingIsH1 && info.h1Count === 1;
}

export function headingPlugin(): Plugin {
  return new Plugin({
    key: headingPluginKey,
    filterTransaction(tr, state) {
      // Allow transactions that don't change the document
      if (!tr.docChanged) return true;

      // Block converting tasks to headings
      for (const step of tr.steps) {
        if (step instanceof ReplaceAroundStep) {
          const oldNode = state.doc.nodeAt(step.from);
          if (oldNode?.type === schema.nodes.task) {
            if (step.slice.content.firstChild?.type === schema.nodes.heading) {
              return false;
            }
          }
        }
      }

      const oldInfo = analyzeHeadings(state.doc);
      const newInfo = analyzeHeadings(tr.doc);

      // If current doc doesn't have valid headings, allow changes (appendTransaction will fix it)
      if (!isValidHeadingStructure(oldInfo)) return true;

      // Allow if result is valid
      if (isValidHeadingStructure(newInfo)) return true;

      // Allow replacing H1 with H1 (e.g., changing H1 content or pasting over it)
      if (oldInfo.h1Count === 1 && newInfo.h1Count === 1) return true;

      return false;
    },
    appendTransaction(transactions: readonly Transaction[], oldState, newState) {
      // Only insert h1 if document doesn't have valid headings (for new documents)
      if (isValidHeadingStructure(analyzeHeadings(newState.doc))) {
        return null;
      }

      const heading = schema.nodes.heading.create(
        { level: 1 },
        schema.text('New document')
      );

      const tr = newState.tr.insert(0, heading);
      return tr;
    },
  });
}
